'use client'

import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useMutation, useQuery } from '@apollo/client'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormItem, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import LanguageSwitcher from '@/components/shared/languageSelect'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import { Icons } from '@/components/shared/icons'
import { useLanguage } from '@/context/LanguageContext'
import { useConfirmation } from '@/context/ConfirmationContext'
import '@/components/styles/Form.css'
import { generateSlug, sanitizeSlug } from '@/containers/Pages/slugUtils'
import {
  DELETE_PAGE_TAG,
  GET_PAGE_TAGS,
  UPSERT_PAGE_TAG,
} from '../pageTagQueries'
import { PageTag, PageTagFormValues } from '../types'

const formSchema = z.object({
  page_tag_profiles: z
    .array(
      z.object({
        id: z.string().optional(),
        language: z.string(),
        name: z.string().optional(),
      })
    )
    .refine(items => items.some(i => i.name?.trim()), {
      message: 'At least one language must have a name',
    }),
  slug: z.string(),
}) satisfies z.ZodType<PageTagFormValues>

interface PageTagFormProps {
  data: {
    id: number | null
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function PageTagForm({
  data,
  onDirtyChange,
  onRequestClose,
}: PageTagFormProps) {
  const { allLanguages, languageId } = useLanguage()
  const [id, setId] = useState(data?.id)
  const { showConfirmation } = useConfirmation()

  // There is no single-tag query in the GraphQL surface, so we read the full
  // list and find the row by id to seed the edit form.
  const { data: { findManyPageTag: tags } = {}, loading: loadingData } =
    useQuery(GET_PAGE_TAGS, {
      fetchPolicy: 'cache-and-network',
      skip: !id,
    })

  const tagData: PageTag | undefined = id
    ? tags?.find((t: PageTag) => t.id === id)
    : undefined

  const form = useForm<PageTagFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      page_tag_profiles: [],
      slug: '',
    },
  })

  const [upsertPageTag] = useMutation(UPSERT_PAGE_TAG, {
    refetchQueries: ['GET_PAGE_TAGS'],
  })

  const [deletePageTag] = useMutation(DELETE_PAGE_TAG, {
    refetchQueries: ['GET_PAGE_TAGS'],
  })

  // Seed the form's profiles array in `allLanguages` order so the form fields
  // addressed by `page_tag_profiles.${languageId}.name` line up. Languages with
  // an existing profile carry its (String) id so a subsequent save edits it in
  // place; missing languages get an id-less blank.
  useEffect(() => {
    if (allLanguages.length === 0) return

    const existingProfiles =
      tagData?.page_tag_profiles?.filter(
        p => p.language && allLanguages.includes(p.language)
      ) ?? []

    form.reset({
      page_tag_profiles: allLanguages.map(lang => {
        const existing = existingProfiles.find(p => p.language === lang)
        if (existing) {
          return {
            id: existing.id,
            language: existing.language,
            name: existing.name ?? '',
          }
        }
        return { language: lang, name: '' }
      }),
      slug: tagData?.slug ?? '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagData, loadingData, allLanguages])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  async function onSubmit(values: PageTagFormValues) {
    const page_tag_profiles = values.page_tag_profiles
      .filter(p => p.name?.trim())
      .map(p => ({
        ...(p.id ? { id: p.id } : {}),
        language: p.language,
        name: p.name,
      }))

    try {
      const response = await upsertPageTag({
        variables: {
          id: id ?? undefined,
          page_tag_profiles,
          slug: values.slug?.trim() || undefined,
        },
      })
      if (id) {
        toast.success('Page tag updated successfully')
      } else {
        toast.success('Page tag created successfully')
        const newId = response.data?.upsertPageTag?.id
        if (newId) setId(newId)
      }
    } catch {
      toast.error('Failed to save page tag')
    }
  }

  const handleDelete = () => {
    if (id) {
      showConfirmation({
        onConfirm: async () => {
          try {
            await deletePageTag({ variables: { where: { id } } })
            toast.success('Page tag deleted successfully')
            onRequestClose?.()
          } catch {
            toast.error('Failed to delete page tag')
          }
        },
        description:
          'Are you sure you want to delete this tag? This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
      })
    }
  }

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Page Tag</SheetTitle>
          <LanguageSwitcher />
        </div>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Tag Name"
                description="This is the display name for this language."
                control={form.control}
                name={`page_tag_profiles.${languageId}.name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input
                        placeholder=""
                        type="text"
                        {...field}
                        value={field.value ?? ''}
                        onChange={e => {
                          const newName = e.target.value
                          const currentSlug = form.getValues('slug')
                          const currentName = field.value ?? ''
                          field.onChange(e)
                          // Keep the slug in sync with the name as long as the
                          // user hasn't manually overridden it (empty, or still
                          // matching what the previous name generated).
                          if (
                            !currentSlug ||
                            currentSlug === generateSlug(currentName)
                          ) {
                            form.setValue('slug', generateSlug(newName), {
                              shouldDirty: true,
                            })
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Slug"
                description="Used in the blog filter URL (?tag=…). Auto-generated from the name if left blank."
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input
                        placeholder="e.g., living-room"
                        type="text"
                        {...field}
                        value={field.value ?? ''}
                        onChange={e =>
                          field.onChange(sanitizeSlug(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Array-root validation error ("At least one language must have a
                  name") attaches at the page_tag_profiles array root, which has
                  no per-field FormMessage, so surface it here. */}
              {(() => {
                const profilesError = form.formState.errors.page_tag_profiles
                const message =
                  profilesError?.root?.message ?? profilesError?.message
                return message ? (
                  <p className="text-sm font-medium text-destructive">
                    {message}
                  </p>
                ) : null
              })()}

              <div className="flex gap-2">
                {id && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleDelete}
                    className="shrink-0"
                  >
                    <Icons.trash className="h-4 w-4 text-red-500" />
                  </Button>
                )}
                <Button type="submit" disabled={loadingData} className="flex-1">
                  {id ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
